import { useContext, useState } from "react";
import { AppContext } from "../App";
import api from "./api/api";
import GearIcon from "../icons/GearIcon";
import logo from "../images/logo.png";
import UserIcon from "../icons/UserIcon";
import ListIcon from "../icons/ListIcon";
import ReportIcon from "../icons/ReportIcon";
import LogoutIcon from "../icons/LogoutIcon";
import Submenu from "./ui/Submenu";
import { useAppStore } from "../store/useAppStore";
import HelpIcon from "../icons/HelpIcon";
import ShopIcon from "../icons/ShopIcon";
import DrawerIcon from "../icons/DrawerIcon";
import LockSmallIcon from "../icons/LockSmallIcon";
import List from "./List";
import { paymentErrorText } from "../functions/paymentErrors";

export default function MainMenu() {
	const {
		apiSettings,
		posSettings,
		setSettingsVisible,
		logout,
		setConfirm,
		setReportsPopUpVisible,
		menuOpen,
		setMenuOpen,
		setOverlay,
		setCashDrawerPopupVisible,
		shop,
		sendCommand,
		user,
		permissions,
		checkPermission,
		askPermission,
		setListCategoriesPopupVisible,
		setListProductsPopupVisible,
		setGloryPopupVisible,
		config,
		sendLocalCommand,
		setPopup,
		shops,
		setShop,
		setList,
		editCart2,
		cart,
		lightMode,
		printAgentStatus,
		thermalPrinters,
		fiscalPrinters,
	} = useContext(AppContext);
	const { glory, cashmatic, unprinted, setInventoryPopupVisible, setFiskalyExpiringLoginVisible } =
		useAppStore();
	const [devicesOpen, setDevicesOpen] = useState(false);
	const isEnabled = (value) => value === 1 || value === "1" || value === true;
	const isSmartCashEnabled = [
		posSettings?.smart_cash,
		posSettings?.smart_pos,
		posSettings?.smart_cassa,
		posSettings?.cassa_smart,
		posSettings?.enable_smart_cash,
		apiSettings?.pos?.smart_cash,
		apiSettings?.pos?.smart_pos,
		apiSettings?.pos?.enable_smart_cash,
		apiSettings?.menu_api?.smart_cash,
		apiSettings?.menu_api?.smart_pos,
		apiSettings?.menu_api?.enable_smart_cash,
		apiSettings?.plugins?.smart_cash?.active,
		apiSettings?.plugins?.smart_pos?.active,
	].some(isEnabled);
	const isPosLightEnabled = isEnabled(apiSettings?.mandant?.pos_light) || isEnabled(lightMode);
	const isGloryEnabled = isEnabled(apiSettings?.mandant?.glory);
	const isCashmaticEnabled = isEnabled(apiSettings?.mandant?.cashmatic);
	const isCashDrawerClosuresEnabled = isEnabled(config?.cash_drawer_closures);
	const hidePosAdvancedControls = isSmartCashEnabled || isPosLightEnabled;
	const hasMenuPermission = (table, fun) => {
		if (!user?.role) return false;
		if (user.role.id == -1) return true;
		return permissions?.[table]?.[fun] == 1;
	};
	const getProtectedMenuProps = (table, fun) =>
		hasMenuPermission(table, fun)
			? {}
			: {
				className: "menu-item-locked",
				endIcon: <LockSmallIcon />,
			};

	const sendThermalPrintersCommand = async (command) => {
		console.log("sendThermalPrintersCommand", command);

		setConfirm({
			title: "Sei sicuro?",
			onConfirm: async () => {
				const res = await api.postProtected("/thermal_printers/send_command/", {
					shop_id: shop.id,
					command: command,
				});
				if (res.success == 0)
					setConfirm({
						title: "Errore",
						message: res.error,
					});
			},
		});
	};

	const runProtectedAction = (table, fun, action) => {
		if (checkPermission(table, fun)) action();
		else askPermission(action);
	};

	// Stato di ogni stampante comunicato dall'agente (controllo periodico di raggiungibilita'): un errore resta
	// visibile finche' l'agente non registra un successo successivo sulla stessa periferica.
	const deviceStatusOf = (device) => {
		if (device.status === "ok" || device.status === "error") return device.status;
		if (device.ok === true) return "ok";
		if (device.ok === false) return "error";
		return "unknown";
	};

	const printAgentInfo = (() => {
		if (!printAgentStatus) return { label: "Server di stampa: non rilevato", status: "unknown" };
		const ageSec = (Date.now() - new Date(printAgentStatus.received_at_utc).getTime()) / 1000;
		const online = ageSec <= 120;
		const versionLabel = printAgentStatus.version ? "v" + printAgentStatus.version : "-";
		const remoteLabel =
			printAgentStatus.remote_version && printAgentStatus.remote_version !== printAgentStatus.version
				? " (online: v" + printAgentStatus.remote_version + ")"
				: "";
		return {
			label: "Server di stampa: " + versionLabel + remoteLabel + " — " + (online ? "Online" : "Offline"),
			status: online ? "online" : "offline",
		};
	})();

	const deviceKindNames = {
		thermal_printer: "Stampante termica",
		fiscal_printer: "Stampante fiscale",
		pos_p17: "POS P17",
		glory: "Glory",
		cashmatic: "Cashmatic",
	};
	const deviceStatusRank = { error: 0, missing: 1, unknown: 2, ok: 3 };

	// Unisce le stampanti configurate nel backoffice (attive) con quelle comunicate dall'agente: una stampante
	// configurata che l'agente non comunica risulta "non rilevata" invece di sparire dalla lista.
	// Le altre periferiche (POS P17, Glory, Cashmatic) si mostrano se l'agente le comunica.
	const printerDevices = (() => {
		const reported = printAgentStatus?.devices || [];
		const configured = [
			...(thermalPrinters || []).map((p) => ({ ...p, kind: "thermal_printer" })),
			...(fiscalPrinters || []).filter((p) => p.pax != 1).map((p) => ({ ...p, kind: "fiscal_printer" })),
		].filter((p) => p.active == 1 || p.active === undefined);
		const printerKinds = ["thermal_printer", "fiscal_printer"];
		const matched = new Set();
		const rows = configured.map((conf) => {
			const index = reported.findIndex(
				(device, i) =>
					!matched.has(i) && device.kind === conf.kind && (String(device.id) === String(conf.id) || (device.ip && device.ip === conf.ip)),
			);
			if (index >= 0) {
				matched.add(index);
				return { device: { ...reported[index], name: reported[index].name || conf.name }, missing: false };
			}
			return { device: { kind: conf.kind, id: conf.id, name: conf.name, ip: conf.ip }, missing: true };
		});
		reported.forEach((device, i) => {
			if (matched.has(i)) return;
			// senza elenco configurato (non ancora caricato) si mostrano comunque le stampanti comunicate dall'agente
			if (configured.length === 0 || !printerKinds.includes(device.kind)) rows.push({ device, missing: false });
		});
		return rows
			.map(({ device, missing }) => {
				// Con agente offline lo stato dell'ultimo heartbeat non e' affidabile.
				const status = printAgentInfo.status != "online" ? "unknown" : missing ? "missing" : deviceStatusOf(device);
				return { device, status };
			})
			.sort((x, y) => deviceStatusRank[x.status] - deviceStatusRank[y.status]);
	})();

	// Riepilogo visibile anche a lista chiusa: conteggio periferiche per stato, in ordine di gravita'.
	const deviceCounts = printerDevices.reduce((acc, { status }) => ({ ...acc, [status]: (acc[status] || 0) + 1 }), {});
	const deviceSummary = [
		{ status: "error", label: (n) => n + (n == 1 ? " errore" : " errori") },
		{ status: "missing", label: (n) => n + (n == 1 ? " non rilevata" : " non rilevate") },
		{ status: "unknown", label: (n) => n + (n == 1 ? " sconosciuta" : " sconosciute") },
		{ status: "ok", label: (n) => (n == printerDevices.length ? n + (n == 1 ? " periferica OK" : " periferiche OK") : n + " OK") },
	].filter(({ status }) => deviceCounts[status] > 0);
	// printerDevices e' ordinato per gravita': il primo determina il colore del riepilogo.
	const worstDeviceStatus = printerDevices[0]?.status || "unknown";
	const hasDevices = printerDevices.length > 0;

	return (
		<div className={"main-menu" + (menuOpen ? " open" : "")}>
			<div>
				<div className="menu-header">
					<div className="icon">
						<UserIcon />
					</div>
					<div className="user">
						<div className="user-name">{user.name + " " + user.surname}</div>
						<div className="user-role">{user?.role?.name || "Operatore"}</div>
					</div>
				</div>

				<div className="menu-content">
					<button
						onClick={() => {
							setMenuOpen(false);
							setOverlay(false);
							setPopup({
								title: "Negozi",
								content: (
									<List
										mode={3}
										elements={
											user.shops.length > 0
												? shops.filter((s) => user.shops.find((us) => us.id == s.id))
												: shops
										}
										onClick={(l) => {
											setPopup(false);
											setShop(l);
											setList(false);
											editCart2({
												shop_id: l.id,
												list_id: null,
											});
											if (cart.table)
												editTable(cart.table.id, {
													list_id: null,
												});
										}}
									/>
								),
							})
						}
						}>
						<ShopIcon /> Cambia negozio: {shop ? shop.name : ""}
					</button>
					<Submenu
						label="Report & chiusura"
						icon={<ReportIcon />}
						visible={true}
						buttons={[
							{
								label: "Reports contabili",
								onClick: () => {
									runProtectedAction("accounting_reports", "view", () => {
										setMenuOpen(false);
										setReportsPopUpVisible(true);
										setOverlay(false);
									});
								},
								visible: true,
								...getProtectedMenuProps("accounting_reports", "view"),
							},
							{
								label: "Ristampa ultimo scontrino",
								onClick: () => {
									runProtectedAction("reprint_last_receipt", "view", () => {
										sendCommand("DUPLICATE");
										setMenuOpen(false);
										setOverlay(false);
									});
								},
								visible: config.fiscal_printer_id,
								...getProtectedMenuProps("reprint_last_receipt", "view"),
							},
							{
								label: "Stampa report giornaliero",
								onClick: () => {
									runProtectedAction("print_daily_report", "view", () => {
										sendCommand("DAILY_FINANCIAL_REPORT");
										setMenuOpen(false);
										setOverlay(false);
									});
								},
								visible: config.fiscal_printer_id,
								...getProtectedMenuProps("print_daily_report", "view"),
							},
							{
								label: "Chiusura fiscale giornaliera",
								onClick: () => {
									runProtectedAction("print_closure", "view", () => {
										sendCommand("DAILY_FISCAL_CLOUSE");
										setMenuOpen(false);
										setOverlay(false);
									});
								},
								visible: config.fiscal_printer_id,
								...getProtectedMenuProps("print_closure", "view"),
							},
							{
								label: "Stampa comande arretrate (" + unprinted + ")",
								onClick: () => {
									runProtectedAction("print_pending_orders", "view", () => {
										sendThermalPrintersCommand("check_unprinted");
										setMenuOpen(false);
										setOverlay(false);
									});
								},
								visible: true,
								...getProtectedMenuProps("print_pending_orders", "view"),
							},
						]}
					/>
					{apiSettings?.mandant?.fiskaly == 1 && (
						<button
							className={
								hasMenuPermission("fiscal_drawer_credentials", "view")
									? ""
									: "menu-item-locked"
							}
							onClick={() => {
								runProtectedAction("fiscal_drawer_credentials", "view", () => {
									setFiskalyExpiringLoginVisible("info");
									setMenuOpen(false);
									setOverlay(false);
								});
							}}
						>
							<span className="menu-item-label">Credenziali Cassetto Fiscale</span>
							{!hasMenuPermission("fiscal_drawer_credentials", "view") && (
								<span className="menu-item-end-icon">
									<LockSmallIcon />
								</span>
							)}
						</button>
					)}
					{isCashDrawerClosuresEnabled && (
						<button
							onClick={() => {
								setCashDrawerPopupVisible(true);
								setMenuOpen(false);
								setOverlay(false);
							}}
						>
							<DrawerIcon /> Chiudi turno
						</button>
					)}
					<Submenu
						label="Glory Device"
						visible={apiSettings?.mandant?.glory == 1 && config.glory_id}
						buttons={[
							{
								label: "Inventario",
								onClick: () => {
									sendLocalCommand("inventoryRequest", { glory_id: glory.id });
									setInventoryPopupVisible(true);
									setMenuOpen(false);
									setOverlay(false);
								},
								visible: true,
							},
							{
								label: "Aggiorna stato (" + glory.status + ")",
								onClick: () => {
									sendLocalCommand("statusRequest", { glory_id: glory.id });
									setMenuOpen(false);
									setOverlay(false);
								},
								visible: true,
							},
						]}
					/>
					<Submenu
						label="Cashmatic Device"
						visible={apiSettings?.mandant?.cashmatic == 1 && config.cashmatic_id}
						buttons={[
							{
								label: "Aggiorna stato (" + (cashmatic ? cashmatic.status : "N/A") + ")",
								onClick: () => {
									sendLocalCommand("statusRequest", { cashmatic_id: cashmatic.id });
									setMenuOpen(false);
									setOverlay(false);
								},
								visible: true,
							},
						]}
					/>
					<Submenu
						label="Gestione menù"
						icon={<ListIcon />}
						visible={isPosLightEnabled}
						buttons={[
							{
								label: "Gestione Categorie",
								onClick: () => {
									setListCategoriesPopupVisible(true);
									setMenuOpen(false);
									setOverlay(false);
								},
								visible: true,
							},
							{
								label: "Gestione Prodotti",
								onClick: () => {
									setListProductsPopupVisible(true);
									setMenuOpen(false);
									setOverlay(false);
								},
								visible: true,
							},
						]}
					/>
					{!hidePosAdvancedControls && isGloryEnabled && (
						<button
							onClick={() => {
								setGloryPopupVisible(true);
								setMenuOpen(false);
								setOverlay(false);
							}}
						>
							Glory Control
						</button>
					)}

					<button
						onClick={() => {
							setSettingsVisible(true);
							setMenuOpen(false);
							setOverlay(false);
						}}
					>
						<GearIcon /> Impostazioni
					</button>
				</div>
			</div>
			<div>
				<div className="section-support">
					<button
						onClick={() => {
							window.open("https://yellgo.atlassian.net/servicedesk/customer/page/62b6f2b9/", "_blank");
						}}
						className="support-portal"
					>
						<HelpIcon /> Richiedi assistenza
					</button>
				</div>
				<div
					className={
						"print-agent-status " +
						printAgentInfo.status +
						(hasDevices ? " clickable" : "") +
						(hasDevices && devicesOpen ? " open" : "")
					}
					{...(hasDevices && {
						role: "button",
						"aria-expanded": devicesOpen,
						onClick: () => setDevicesOpen((v) => !v),
					})}
				>
					<div className="print-agent-label">{printAgentInfo.label}</div>
					{hasDevices && (
						<div className={"device-summary " + worstDeviceStatus}>
							{deviceSummary.map(({ status, label }) => (
								<span key={status} className={"device-summary-item " + status}>
									<span className="device-dot" />
									{label(deviceCounts[status])}
								</span>
							))}
							<span className="device-toggle">▸</span>
						</div>
					)}
				</div>
				{hasDevices && devicesOpen && (
					<div className="device-list">
						{printerDevices.map(({ device, status }) => {
							const name = device.name || deviceKindNames[device.kind] || "Periferica";
							const stateLabel =
								status == "ok"
									? device.detail || "OK"
									: status == "error"
									? "Errore"
									: status == "missing"
									? "Non rilevata"
									: "Sconosciuto";
							const note = status == "error" ? paymentErrorText(device.last_error_code) || device.last_error_message || "" : "";
							return (
								<div key={device.kind + ":" + (device.id ?? device.ip)} className={"device-line " + status}>
									<span className="device-dot" />
									<span className="device-name">{name}</span>
									<span className="device-state">{stateLabel}</span>
									{note && <div className="device-note">{note}</div>}
								</div>
							);
						})}
					</div>
				)}
				<div className="menu-footer">
					<img src={logo} className="logo" />

					<span>{localStorage.getItem("pos-version")}</span>
					<button
						onClick={() => {
							setConfirm({
								title: "Log Out?",
								onConfirm: () => {
									logout();
									setMenuOpen(false);
									setOverlay(false);
								},
							});
						}}
					>
						<LogoutIcon />
						Esci
					</button>
				</div>
			</div>
		</div>
	);
}
